// GtPipeline — everyone on a role, in the role's own stages.
// Moving someone here also updates what they see in their portal and the
// submission the fees hang off. Nobody is emailed by a move: when the
// candidate's portal changes, the "Tell the candidate?" box asks first.
// Two views over the same data: a list that scales to hundreds, and a board for
// when the numbers are small enough that seeing the shape is worth the space.
import { gtConfirm } from '../../components/GtConfirm';
import { useState, useEffect, useCallback } from 'react';
import GtLoader from '../../components/GtLoader';
import GtNoticeModal from '../../components/GtNoticeModal';
import GtBulkNoticeModal from '../../components/GtBulkNoticeModal';
import GtSendFormModal from '../../components/GtSendFormModal';
import GtEmailModal from '../../components/GtEmailModal'; // C1-EMAIL
import GtSequenceStart from '../../components/GtSequenceStart'; // C1-SEQ2
import { TempDot } from '../../components/gtTemp';
import GtShareCreate from './GtShareCreate';
import { useDeepMatch, searchPool, SearchMatchLine } from '../../components/gtSearch'; // C1-SEARCH
import { GtSelect } from '../../components/GtControls';
import { useNavigate } from 'react-router-dom';
import GtAddToBenchModal from '../../components/GtAddToBenchModal';
// C1-STAGES: rename, reorder, colour, add and remove this role's stages.
import GtStageEditor from '../../components/GtStageEditor';

// Buckets belong to the role, not to this file. Named tones keep the palette
// in one place while the stages themselves can be anything.
const TONE_HEX = {
  teal: '#17838A', slate: '#64748B', blue: '#1565C0', purple: '#6A1B9A',
  amber: '#B26B00', green: '#056F4E', rose: '#BE123C',
};

function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }
function since(iso) {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d < 1) return 'today';
  if (d === 1) return '1 day';
  if (d < 30) return d + ' days';
  const m = Math.floor(d / 30);
  return m + (m === 1 ? ' month' : ' months');
}

// C1-SEQ2: where this person is with sequences on this role.
const CS_TONE = { queued: 'draft', writing: 'draft', ready: 'draft', failed: 'draft', approved: 'live', sending: 'live', paused: 'live',
  replied: 'replied', bounced: 'stopped', unsubscribed: 'stopped', stopped: 'stopped', finished: 'done' };
function ContactPill({ c, onOpen }) {
  if (!c) return <span className="gtsq-cs gtsq-cs--none">Not contacted</span>;
  return (
    <button type="button" className={'gtsq-cs gtsq-cs--' + (CS_TONE[c.status] || 'done')} title="Open the sequence" onClick={() => onOpen(c.run_id)}>
      {c.label}
    </button>
  );
}

export default function GtPipeline({ roleId, authFetch, flash, onOpenCandidate, clientId, getToken, roleDbId, roleTitle }) {
  const [rows, setRows] = useState(null);
  const [stages, setStages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [q, setQ] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [moving, setMoving] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [pool, setPool] = useState([]);
  const [poolQ, setPoolQ] = useState('');
  const [poolLoading, setPoolLoading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [noticeIds, setNoticeIds] = useState(null);
  // Bulk: tick people in the list, move them all, then one "Tell them?" box.
  const [selected, setSelected] = useState([]);
  const [bulkStage, setBulkStage] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkNoticeIds, setBulkNoticeIds] = useState(null);
  const [formFor, setFormFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null); // C1-EMAIL
  const [seqFor, setSeqFor] = useState(null); // C1-SEQ2
  const [contact, setContact] = useState({}); // C1-SEQ2: sequence status per person on this role
  const [shareFor, setShareFor] = useState(null);
  const [benchFor, setBenchFor] = useState(null);
  const [editStages, setEditStages] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, st, cs] = await Promise.all([
        authFetch('/api/roles/' + roleId + '/pipeline'),
        authFetch('/api/roles/' + roleId + '/stages'),
        authFetch('/api/roles/' + roleId + '/contact-status').catch(function () { return { people: [] }; }), // C1-SEQ2
      ]);
      const csm = {}; (cs.people || []).forEach(function (x) { csm[x.candidate_id] = x; }); setContact(csm);
      setRows(r.pipeline || []);
      // Anyone no longer on the role drops out of the selection.
      setSelected(function (sel) { return sel.filter(function (id) { return (r.pipeline || []).some(function (x) { return x.id === id; }); }); });
      setStages(st.stages || []);
    } catch (e) { setRows([]); setStages([]); }
    setLoading(false);
  }, [authFetch, roleId]);

  useEffect(() => { load(); }, [load]);

  const loadPool = useCallback(async () => {
    setPoolLoading(true);
    try {
      setPool(await searchPool(authFetch, poolQ, 40)); // C1-SEARCH
    } catch (e) { setPool([]); }
    setPoolLoading(false);
  }, [authFetch, poolQ]);

  useEffect(() => { if (adding) { const t = setTimeout(loadPool, 250); return () => clearTimeout(t); } }, [adding, loadPool]);
  // C1-SEARCH: the pipeline box also finds people by anything in their record.
  const deep = useDeepMatch(authFetch, q, (rows || []).map(function (r) { return r.candidate_id; }));

  async function move(entry, stage, reason) {
    // Any way out (Rejected, Withdrawn, or a bucket of your own) needs a
    // reason, and the person goes to the bucket they were dropped on.
    const target = (stages || []).find(function (x) { return x.key === stage; });
    if (target && target.outcome === 'lost' && !(reason && reason.trim())) {
      setRejectFor({ id: entry.id, stage: stage }); setRejectReason(''); return;
    }
    setMoving(entry.id);
    try {
      const r = await authFetch('/api/pipeline/' + entry.id, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage, rejected_reason: reason || null }),
      });
      setRejectFor(null); await load();
      if (target && target.meaning === 'placed') flash('Placed. Record the fee on the Submissions tab.');
      if (r && r.notice) setNoticeIds([r.notice.id]);
    } catch (e) { flash(e.message); }
    setMoving(null);
  }

  function toggleSelect(id) {
    setSelected(function (sel) { return sel.indexOf(id) === -1 ? sel.concat([id]) : sel.filter(function (x) { return x !== id; }); });
  }

  async function runBulk() {
    const target = (stages || []).find(function (x) { return x.key === bulkStage; });
    if (!target || !selected.length) return;
    if (target.outcome === 'lost' && !bulkReason.trim()) { flash('Add a short reason first.'); return; }
    setBulkBusy(true); setBulkResult(null);
    try {
      const r = await authFetch('/api/roles/' + roleId + '/pipeline/move', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_ids: selected, stage: bulkStage, rejected_reason: bulkReason || null }),
      });
      setBulkResult(r);
      setSelected([]); setBulkStage(''); setBulkReason('');
      await load();
      if (r.notices && r.notices.length === 1) setNoticeIds(r.notices);
      else if (r.notices && r.notices.length > 1) setBulkNoticeIds(r.notices);
    } catch (e) { flash(e.message); }
    setBulkBusy(false);
  }

  async function addToPipeline(candidateId) {
    try {
      const r = await authFetch('/api/roles/' + roleId + '/pipeline', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: [candidateId] }),
      });
      await load(); flash('Added to pipeline');
      if (r && r.notices && r.notices.length) setNoticeIds(r.notices.map(function (n) { return n.id; }));
    } catch (e) { flash(e.message); }
  }

  async function removeFromPipeline(id) {
    const confirmed = await gtConfirm({
      title: 'Remove from pipeline',
      message: 'This takes the candidate off this role. Their record is not affected.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!confirmed) return;
    try { await authFetch('/api/pipeline/' + id, { method: 'DELETE' }); await load(); flash('Removed'); }
    catch (e) { flash(e.message); }
  }

  if (rows === null || stages === null) return <GtLoader compact />;

  // Derived from the role's own buckets, so renamed or added stages just work.
  const SOURCING = stages.filter(function (x) { return x.side === 'internal'; }).map(function (x) { return x.key; });
  const CLIENT_SIDE = stages.filter(function (x) { return x.side === 'client'; }).map(function (x) { return x.key; });
  const ALL = stages.map(function (x) { return x.key; });
  const LABEL = {};
  const TONE = {};
  const OUTCOME = {};
  stages.forEach(function (x) { LABEL[x.key] = x.label; TONE[x.key] = TONE_HEX[x.tone] || '#64748B'; OUTCOME[x.key] = x.outcome; });

  const ql = q.trim().toLowerCase();
  const filtered = rows.filter(function (r) {
    if (ql) {
      const hay = [r.full_name, r.candidate_ref, r.current_company, r.headline].join(' ').toLowerCase();
      if (hay.indexOf(ql) === -1 && !deep.ids.has(r.candidate_id)) return false;
    }
    if (stageFilter !== 'all' && r.stage !== stageFilter) return false;
    return true;
  });

  const counts = {};
  ALL.forEach(function (st) { counts[st] = rows.filter(function (r) { return r.stage === st; }).length; });
  // Each person counted once, by what their bucket means: still working,
  // with the client, placed, or out.
  const isOpen = function (st) { return OUTCOME[st] === 'open'; };
  const inSourcing = rows.filter(function (r) { return SOURCING.indexOf(r.stage) !== -1 && isOpen(r.stage); }).length;
  const withClient = rows.filter(function (r) { return CLIENT_SIDE.indexOf(r.stage) !== -1 && isOpen(r.stage); }).length;
  const placedCount = rows.filter(function (r) { return OUTCOME[r.stage] === 'won'; }).length;
  const outCount = rows.filter(function (r) { return OUTCOME[r.stage] === 'lost'; }).length;

  const stageOpts = ALL.map(function (st) { return { value: st, label: LABEL[st] }; });
  const rejectTarget = rejectFor ? stages.find(function (x) { return x.key === rejectFor.stage; }) : null;
  const rejectWithdraw = rejectTarget && rejectTarget.meaning === 'withdrawn';

  function card(r, compact) {
    const isRejecting = !!rejectFor && rejectFor.id === r.id;
    return (
      <div key={r.id}
        className={'gtpl-card' + (compact ? ' gtpl-card--compact' : '') + (moving === r.id ? ' gtpl-card--busy' : '') + (dragId === r.id ? ' gtpl-card--dragging' : '')}
        draggable={compact}
        onDragStart={compact ? (e) => { setDragId(r.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
        onDragEnd={compact ? () => { setDragId(null); setDragOver(null); } : undefined}>
        <div className="gtpl-cardhead">
          {compact && <span className="gtpl-grip" aria-hidden="true">⠿</span>}
          {!compact && (
            <input type="checkbox" className="gtpb-check" checked={selected.indexOf(r.id) !== -1}
              onChange={() => toggleSelect(r.id)} aria-label={'Select ' + r.full_name} />
          )}
          <span className="gtpl-av" style={{ background: avatarColour(r.full_name) }}>{initials(r.full_name)}</span>
          <div className="gtpl-who">
            <span className="gtpl-namerow"><button type="button" className="gtpl-name" onClick={() => onOpenCandidate && onOpenCandidate(r.candidate_ref || r.candidate_id)}>{r.full_name}</button><TempDot row={r} /></span>{/* C1-EMAIL */}
            {!compact && <div className="gtpl-sub">{[r.current_title, r.current_company].filter(Boolean).join(' · ') || r.headline || '—'}</div>}
          </div>
          {!compact && <span className="gtr-refbadge gtr-refbadge-sm">{r.candidate_ref}</span>}
        </div>

        {!compact && (
          <div className="gtpl-meta">
            <span className="gtpl-stagepill" style={{ background: TONE[r.stage] + '14', color: TONE[r.stage] }}>{LABEL[r.stage] || r.stage}</span>
            <span className="gtpl-age">in stage {since(r.stage_changed_at)}</span>
            <ContactPill c={contact[r.candidate_id]} onOpen={(runId) => navigate('/sequences/' + runId)} />{/* C1-SEQ2 */}
            {r.location && <span className="gtpl-age">· {r.location}</span>}
            {r.from_bench_ref && (
              <button type="button" className="gtbn-frombench" onClick={() => navigate('/benches/' + r.from_bench_ref)}
                title={'Open the bench ' + r.from_bench_name}>From bench: {r.from_bench_name}</button>
            )}
          </div>
        )}

        {r.rejected_reason && <div className="gtpl-reason">{r.rejected_reason}</div>}

        {isRejecting ? (
          <div className="gtpl-reject">
            <input value={rejectReason} autoFocus
              aria-label={'Reason for moving to ' + (rejectTarget ? rejectTarget.label : 'this stage')}
              placeholder={rejectWithdraw ? 'Why did they pull out? One line.' : 'Why are they out? One line.'}
              onChange={e => setRejectReason(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && rejectReason.trim()) move(r, rejectFor.stage, rejectReason);
                if (e.key === 'Escape') setRejectFor(null);
              }} />
            <button className="gtc-btn-ghost" onClick={() => setRejectFor(null)}>Cancel</button>
            <button className="gtc-primary" disabled={!rejectReason.trim()} onClick={() => move(r, rejectFor.stage, rejectReason)}>
              {'Move to ' + (rejectTarget ? rejectTarget.label : 'this stage')}
            </button>
          </div>
        ) : (
          <div className="gtpl-acts">
            {!compact && (
              <div className="gtpl-move">
                <GtSelect value={r.stage} options={stageOpts} onChange={v => move(r, v)} />
              </div>
            )}
            {compact && <span className="gtpl-draghint">drag to move</span>}
            {!compact && (
              <>
                <button className="gtr-editbtn gtr-editbtn-ghost" onClick={() => onOpenCandidate && onOpenCandidate(r.candidate_ref || r.candidate_id)}>Open</button>
                <button className="gtr-editbtn gtr-editbtn-ghost gtr-danger" onClick={() => removeFromPipeline(r.id)}>Remove</button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="gtpl">
      <div className="gtpl-strip">
        <div className="gtpl-stat"><span>In pipeline</span><b>{rows.length}</b></div>
        <div className="gtpl-stat gtpl-stat--teal"><span>Sourcing</span><b>{inSourcing}</b></div>
        <div className="gtpl-stat gtpl-stat--blue"><span>With client</span><b>{withClient}</b></div>
        <div className="gtpl-stat gtpl-stat--green"><span>Placed</span><b>{placedCount}</b></div>
        <div className="gtpl-stat gtpl-stat--rose"><span>Out</span><b>{outCount}</b></div>
        <div className="gtpl-spacer" />
        <div className="gtpl-viewtoggle">
          <button className={view === 'list' ? 'gtpl-vt--on' : ''} onClick={() => setView('list')}>List</button>
          <button className={view === 'board' ? 'gtpl-vt--on' : ''} onClick={() => setView('board')}>Board</button>
        </div>
        <button className="gtc-primary" onClick={() => { setAdding(true); setPoolQ(''); }}>Add candidates</button>
      </div>

      <div className="gtpl-filters">
        <input className="gtsb-search" placeholder="Search this pipeline…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="gtpl-chips">
          <button className={'gtsb-chip' + (stageFilter === 'all' ? ' gtsb-chip--on' : '')} onClick={() => setStageFilter('all')}>All {rows.length}</button>
          {ALL.map(st => (
            <button key={st} className={'gtsb-chip' + (stageFilter === st ? ' gtsb-chip--on' : '')} onClick={() => setStageFilter(st)}>
              {LABEL[st]} {counts[st] ? <b>{counts[st]}</b> : null}
            </button>
          ))}
          <button type="button" className="gtse-chip" onClick={() => setEditStages(true)} title="Rename, reorder, colour, add or remove this role's stages">
            <span aria-hidden="true">⚙</span> Manage stages
          </button>
        </div>
        <button className="gtsb-refresh" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      {rows.length === 0 ? (
        <div className="gtsb-empty">
          <div className="gtsb-empty-mark">◆</div>
          <h3>Nobody in the pipeline yet</h3>
          <p>Add candidates from the pool to start sourcing for this role. They move through your stages before the client ever sees them.</p>
        </div>
      ) : view === 'list' ? (
        filtered.length === 0
          ? <div className="gtr-emptytab">No candidates match this filter.</div>
          : (
            <>
              <div className={'gtpb-bar' + (selected.length ? ' gtpb-bar--on' : '')}>
                <label className="gtpb-all">
                  <input type="checkbox" className="gtpb-check"
                    checked={filtered.length > 0 && filtered.every(function (r) { return selected.indexOf(r.id) !== -1; })}
                    onChange={e => {
                      const shown = filtered.map(function (r) { return r.id; });
                      setSelected(function (sel) {
                        return e.target.checked
                          ? Array.from(new Set(sel.concat(shown)))
                          : sel.filter(function (id) { return shown.indexOf(id) === -1; });
                      });
                    }} />
                  <span>{selected.length ? selected.length + ' selected' : 'Select all ' + filtered.length + ' shown'}</span>
                </label>
                {selected.length > 0 && (
                  <>
                    <span className="gtpb-label">Move to</span>
                    <div className="gtpb-stage"><GtSelect value={bulkStage} options={stageOpts} onChange={setBulkStage} placeholder="Choose a stage" /></div>
                    {(function () {
                      const t = (stages || []).find(function (x) { return x.key === bulkStage; });
                      return t && t.outcome === 'lost' ? (
                        <input className="gtpb-reason" value={bulkReason} onChange={e => setBulkReason(e.target.value)}
                          aria-label="Reason for everyone selected"
                          placeholder={t.meaning === 'withdrawn' ? 'Why did they pull out? Applies to all.' : 'Why are they out? Applies to all.'} />
                      ) : null;
                    })()}
                    <button className="gtc-primary" disabled={bulkBusy || !bulkStage} onClick={runBulk}>
                      {bulkBusy ? 'Moving…' : 'Move ' + selected.length}
                    </button>
                    <button className="gtc-btn-ghost" disabled={bulkBusy}
                      onClick={() => setBenchFor(rows.filter(function (x) { return selected.indexOf(x.id) !== -1; }).map(function (x) { return x.candidate_id; }))}>
                      Add to bench
                    </button>
                    <button className="gtc-btn-ghost" disabled={bulkBusy}
                      onClick={() => setEmailFor(rows.filter(function (x) { return selected.indexOf(x.id) !== -1; }).map(function (x) { return x.candidate_id; }))}>
                      Email
                    </button>{/* C1-EMAIL */}
                    <button className="gtc-btn-ghost" disabled={bulkBusy}
                      onClick={() => setSeqFor(rows.filter(function (x) { return selected.indexOf(x.id) !== -1; }).map(function (x) { return x.candidate_id; }))}>
                      Start sequence
                    </button>{/* C1-SEQ2 */}
                    <button className="gtc-btn-ghost" disabled={bulkBusy}
                      onClick={() => setFormFor(rows.filter(function (x) { return selected.indexOf(x.id) !== -1; }).map(function (x) { return x.candidate_id; }))}>
                      Send form
                    </button>
                    <button className="gtc-btn-ghost" disabled={bulkBusy}
                      title={clientId ? 'Share the ticked profiles with the client' : 'This role has no client attached'}
                      onClick={() => {
                        if (!clientId) { flash('This role has no client attached. Add one on the role first.'); return; }
                        setShareFor(rows.filter(function (x) { return selected.indexOf(x.id) !== -1; }).map(function (x) {
                          return { id: x.candidate_id, full_name: x.full_name, ref: x.candidate_ref || x.ref, headline: x.headline,
                            current_title: x.current_title, current_company: x.current_company, location: x.location, consent_status: x.consent_status };
                        }));
                      }}>
                      Share with client
                    </button>
                    <button className="gtc-btn-ghost" disabled={bulkBusy} onClick={() => { setSelected([]); setBulkStage(''); setBulkReason(''); }}>Clear</button>
                  </>
                )}
              </div>
              {shareFor && (
                <GtShareCreate clientId={clientId} client={null} roleId={roleId} getToken={getToken} preselected={shareFor}
                  onClose={() => { setShareFor(null); load(); }}
                  onCreated={() => { setSelected([]); load(); }} />
              )}
              {emailFor && ( // C1-EMAIL
                <GtEmailModal candidateIds={emailFor} source={{ kind: 'role', id: roleDbId || roleId, label: roleTitle || null }} authFetch={authFetch} onClose={() => { setEmailFor(null); load(); }} />
              )}
              {seqFor && ( // C1-SEQ2
                <GtSequenceStart candidateIds={seqFor} roleId={roleDbId || roleId} roleTitle={roleTitle || null} source={{ kind: 'role', id: roleDbId || null, label: roleTitle || null }}
                  authFetch={authFetch} navigate={navigate} onClose={() => { setSeqFor(null); load(); }} />
              )}
              {formFor && (
                <GtSendFormModal candidateIds={formFor} source={{ kind: 'role', id: roleId }} authFetch={authFetch} onClose={() => setFormFor(null)} />
              )}
              {bulkResult && (
                <div className={'gtnt-banner ' + (bulkResult.failed.length ? 'gtnt-banner--amber' : 'gtnt-banner--green') + ' gtpb-result'}>
                  <b>{bulkResult.moved.length} moved to {bulkResult.stage.label}.</b>
                  {bulkResult.unchanged.length > 0 && ' ' + bulkResult.unchanged.length + ' were already there.'}
                  {bulkResult.failed.length > 0 && (
                    <> {bulkResult.failed.length} could not be moved:
                      <ul className="gtnt-blocked">
                        {bulkResult.failed.map(x => <li key={x.entry_id}><b>{x.name || 'Someone'}</b> {x.reason}</li>)}
                      </ul>
                    </>
                  )}
                  <button className="gtpb-dismiss" onClick={() => setBulkResult(null)} aria-label="Dismiss">×</button>
                </div>
              )}
              <div className="gtpl-list">{filtered.map(r => card(r, false))}</div>
            </>
          )
      ) : (
        <div className="gtpl-board">
          {SOURCING.concat(CLIENT_SIDE.filter(function (st) { return counts[st] > 0 || OUTCOME[st] === 'open' || dragId != null; })).map(st => {
            const col = filtered.filter(r => r.stage === st);
            return (
              <div key={st}
                className={'gtpl-col' + (dragOver === st ? ' gtpl-col--over' : '') + (CLIENT_SIDE.indexOf(st) !== -1 ? ' gtpl-col--client' : '')}
                onDragOver={(e) => { e.preventDefault(); if (dragOver !== st) setDragOver(st); }}
                onDragLeave={() => setDragOver(o => (o === st ? null : o))}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(null);
                  const entry = rows.find(x => x.id === dragId);
                  setDragId(null);
                  if (entry && entry.stage !== st) move(entry, st);
                }}>
                <div className="gtpl-colhead" style={{ borderTopColor: TONE[st] }}>
                  <span>{LABEL[st]}</span>
                  <b>{col.length}</b>
                </div>
                <div className="gtpl-colbody">
                  {col.length === 0 ? <div className="gtpl-colempty">—</div> : col.map(r => card(r, true))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editStages && (
        <GtStageEditor roleId={roleDbId || roleId} roleTitle={roleTitle} authFetch={authFetch} flash={flash}
          onClose={() => setEditStages(false)} onSaved={() => { setEditStages(false); setStageFilter('all'); load(); }} />
      )}

      {benchFor && (
        <GtAddToBenchModal candidateIds={benchFor} authFetch={authFetch} flash={flash} navigate={navigate}
          source="role" sourceRoleId={Number(roleId) || null}
          onClose={() => setBenchFor(null)} onDone={() => setSelected([])} />
      )}

      {noticeIds && (
        <GtNoticeModal ids={noticeIds} authFetch={authFetch} onClose={() => setNoticeIds(null)} />
      )}

      {bulkNoticeIds && (
        <GtBulkNoticeModal ids={bulkNoticeIds} authFetch={authFetch} onClose={() => setBulkNoticeIds(null)} />
      )}

      {adding && (
        <div className="gtc-modal-backdrop" onClick={() => setAdding(false)}>
          <div className="gtc-modal gtsh-modal" onClick={e => e.stopPropagation()}>
            <div className="gtc-modal-head">Add candidates to this pipeline</div>
            <div className="gtc-modal-body gtsh-body">
              <input className="gtsh-search" placeholder="Search the pool: skills, CV, notes, names… or describe who you need" value={poolQ} onChange={e => setPoolQ(e.target.value)} />
              <div className="gtsh-picklist">
                {poolLoading ? <div className="gtr-empty-sm">Searching…</div> : (
                  pool.length === 0 ? <div className="gtr-empty-sm">No candidates found.</div> : pool.map(c => {
                    const already = rows.some(r => r.candidate_id === c.id);
                    return (
                      <button type="button" key={c.id} className={'gtsh-pickrow' + (already ? ' gtsh-pickrow--blocked' : '')} disabled={already} onClick={() => addToPipeline(c.id)}>
                        <span className="gtsh-pickav" style={{ background: avatarColour(c.full_name) }}>{initials(c.full_name)}</span>
                        <span className="gtsh-pickbody">
                          <span className="gtsh-pickname">{c.full_name}{c.ref && <span className="gtsh-pickref">{c.ref}</span>}</span>
                          <span className="gtsh-picksub">{[c.current_title, c.current_company, c.location].filter(Boolean).join(' · ') || c.headline || '—'}</span>
                          {c.search_match && <SearchMatchLine m={c.search_match} />}
                        </span>
                        {already && <span className="gtsh-stage">already added</span>}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            <div className="gtc-modal-foot"><button className="gtc-primary" onClick={() => setAdding(false)}>Done</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
